import * as d3 from 'd3';
import { BarChartConfig } from '../data/BarChartConfig';
import { LineChartConfig } from '../data/LineChartConfig';
import { DataRenderer } from './DataRenderer';
import { Rectangle } from './Rectange';
import { ChartTooltipContent } from './ChartTooltipContent';
import { ChartRenderer } from './ChartRenderer';
import { ChartDataType } from '../data/ChartDataType';
import { MeasureUnitInfo } from '../../measure-unit/MeasureUnitInfo';
import { ChartDataComparisonType } from '../data/ChartDataComparisonType';
import { ChartTooltipData, ChartTooltipSeriesData } from './ChartTooltipData';
import { MeasureUnitCode } from '../../measure-unit/MeasureUnitCode';
import { CurrencyCode } from 'context/currency';

export class TooltipRenderer {
  constructor(
    protected config: LineChartConfig | BarChartConfig,
    private tooltipContent: ChartTooltipContent,
    private dataRenderers: DataRenderer[],
    private sortItems?: boolean,
    private removeOuterSpacing?: boolean
  ) {}

  public render(
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    innerRect: Rectangle,
    scaleBand: d3.ScaleBand<string>,
    linearScale: d3.ScaleLinear<number, number>,
    limit?: number,
    inverted?: boolean,
    scaleBandIsYAxis?: boolean,
    secondaryLinearScale?: d3.ScaleLinear<number, number>
  ) {
    const bandWidth = scaleBand.step();
    let lastI = -1;
    let ix: number;

    const primaryData = this.config.getDataArray(ChartDataType.primary)!.getData(limit, inverted)!;

    svg
      .append('rect')
      .attr('transform', `translate( ${innerRect.x} , ${innerRect.y})`)
      .attr('class', 'chart-overlay')
      .attr('width', Math.max(innerRect.width, 0))
      .attr('height', Math.max(innerRect.height, 0))
      .on('mouseout', () => {
        // Time out is need so it does not interfere with the mouse move event
        setTimeout(() => {
          this.dataRenderers.forEach(dataRenderer => {
            dataRenderer.switchHighlight(ix, false);
          });

          this.tooltipContent.close();
          lastI = -1;
        });
      })
      .on('mousemove', e => {
        if (!this.config.showTooltips) {
          return;
        }

        const mouse: [number, number] = d3.pointer(e);
        const innerx: number = mouse[0];
        const innery: number = mouse[1];

        const xDomainLen = scaleBand.domain().length;

        ix = scaleBandIsYAxis ? xDomainLen - 1 - Math.floor(innery / bandWidth) : Math.floor(innerx / bandWidth);

        if (ix === -1) {
          ix = 0;
        } else if (ix > xDomainLen - 1) {
          ix = xDomainLen - 1;
        }

        if (lastI !== ix) {
          this.dataRenderers.forEach(dataRenderer => {
            dataRenderer.switchHighlight(ix, true);
          });

          const { hasCustomAnchor, yValue } = this.initTooltipData(ix, this.config, limit, inverted);

          const scaleBandVal =
            scaleBand(primaryData[ix]!.label!)! +
            ChartRenderer.getBandSpacing(scaleBand, ix, !!this.removeOuterSpacing);

          let scaleLinearVal = linearScale(yValue);

          const isSecondaryDataAsLine = (this.config as any).secondaryAsLine;

          if (isSecondaryDataAsLine && secondaryLinearScale && !hasCustomAnchor) {
            const secDataArray = this.config.getDataArray(ChartDataType.secondary);
            const secDataPoint = secDataArray?.getData(limit, inverted)[ix];

            if (secDataPoint && secDataPoint.value !== undefined) {
              scaleLinearVal = secondaryLinearScale(secDataPoint.value);
            }
          }

          const dy = scaleBandIsYAxis ? scaleBandVal : scaleLinearVal;
          const dx = scaleBandIsYAxis ? scaleLinearVal : scaleBandVal;

          this.tooltipContent.open(innerRect.x + dx, innerRect.y + dy);
          lastI = ix;
        }
      });
  }

  protected initTooltipData(
    index: number,
    config: LineChartConfig | BarChartConfig,
    limit: number | undefined,
    inverted: boolean | undefined
  ): { hasCustomAnchor: boolean; yValue: number } {
    const dataArray = config.getDataArray(ChartDataType.primary)!;

    const data = dataArray.getData(limit, inverted)[index];

    let yValue = data.value;

    const unit = this.config.value!.unit;
    const unitInfo: MeasureUnitInfo | undefined = typeof unit === 'string' ? undefined : unit;
    const unitCode = unitInfo?.code ?? (unit as MeasureUnitCode);

    const tooltipData: ChartTooltipData = {
      index,
      label: data.label,
      seriesData: this.buildSeriesTooltipData(index, config, limit, inverted),
    };

    tooltipData.values = {
      unit: unitInfo ?? { code: unitCode },
      interpretation: config.value!.interpretation,
      formatConfig: config.value!.formatConfig,
    };

    tooltipData.headerData = {
      label: data.label!,
    };

    if (!config.breakdownMode || config.breakdownMode === 'band') {
      if (unitInfo?.isOriginalCurrency && dataArray.containsCurrency) {
        tooltipData.values.unit = new MeasureUnitInfo(unitInfo.code, true, data.label as CurrencyCode);
      }
      tooltipData.data = {
        label: this.config.value!.label!,
        value: data.value!,
      };

      // compare data
      const compareData = config.getDataArray(ChartDataType.primary, ChartDataComparisonType.comparison);
      if (compareData) {
        tooltipData.withComparison = true;
        tooltipData.comparisonLabel = compareData.label;
        const d = compareData.getData(limit, inverted)[index];
        tooltipData.data.comparisonValue = d ? d.value! : undefined;
      }

      // plan data
      const planData = this.config.getDataArray(ChartDataType.primary, ChartDataComparisonType.plan);
      if (planData) {
        tooltipData.plan = planData.label;
        const d = planData.getData(limit, inverted)[index];
        tooltipData.data.planValue = d ? d.value : undefined;
      }

      // secondary data
      const secondaryData = config.getDataArray(ChartDataType.secondary);
      if (secondaryData) {
        const additionalUnit = config.additionalValue!.unit;
        const additionalUnitInfo: MeasureUnitInfo | undefined =
          typeof additionalUnit === 'string' ? undefined : additionalUnit;
        const additionalUnitCode = additionalUnitInfo?.code ?? (additionalUnit as MeasureUnitCode);

        tooltipData.secondaryValues = {
          unit: additionalUnitInfo ?? { code: additionalUnitCode },
          interpretation: config.additionalValue!.interpretation,
        };

        if (!config.additionalValue?.label) {
          tooltipData.headerData.caption = config.value?.label;
        }

        if (additionalUnitInfo?.isOriginalCurrency && dataArray.containsCurrency) {
          tooltipData.secondaryValues.unit = new MeasureUnitInfo(
            additionalUnitInfo.code,
            true,
            data.label as CurrencyCode
          );
        }

        const s = secondaryData.getData(limit, inverted)[index];
        tooltipData.secondaryData = {
          label: secondaryData.label,
          value: s ? s.value : undefined,
        };
        tooltipData.data.label = dataArray.label;

        const secondaryCompare = config.getDataArray(ChartDataType.secondary, ChartDataComparisonType.comparison);
        if (secondaryCompare) {
          const d = secondaryCompare.getData(limit, inverted)[index];
          tooltipData.secondaryData.comparisonValue = d ? d.value : undefined;
        }

        const secondaryPlanData = this.config.getDataArray(ChartDataType.secondary, ChartDataComparisonType.plan);
        if (secondaryPlanData) {
          const d = secondaryPlanData.getData(limit, inverted)[index];
          tooltipData.secondaryData.planValue = d ? d.value : undefined;
        }
      }
    } else {
      // breakdown data
      tooltipData.headerData.value = data.value;

      const breakDownDataRaw = config.data!.filter(array => array.type === ChartDataType.breakdown);
      const total = breakDownDataRaw
        .map(country => country.getData(limit, inverted)[index].value!)
        .reduce((sum, current) => sum + current, 0);

      tooltipData.breakdownData = breakDownDataRaw.map(array => {
        let breakdownUnitInfo = unitInfo;

        if (breakdownUnitInfo?.isOriginalCurrency && !breakdownUnitInfo.currencyCode && array.isCurrencyBreakdown) {
          breakdownUnitInfo = new MeasureUnitInfo(breakdownUnitInfo.code, true, array.label as CurrencyCode);
        }

        const value = array.getData(limit, inverted)[index].value!;
        const isOther = !!array.isOther;

        return {
          label: array.label,
          percentage: value / total,
          value: value,
          unit: breakdownUnitInfo,
          chartColor: array.color,
          isOther: isOther,
        };
      });

      tooltipData.headerData.sum = total;
      tooltipData.headerData.caption = config.value?.label ?? '';

      const secondaryDataArray = config.getDataArray(ChartDataType.secondary);

      if (secondaryDataArray) {
        const secDataPoint = secondaryDataArray.getData(limit, inverted)[index];
        const addUnit = config.additionalValue?.unit;

        const addUnitInfo = typeof addUnit === 'string' ? { code: addUnit } : addUnit;
        tooltipData.secondaryValues = {
          unit: addUnitInfo as MeasureUnitInfo,
          interpretation: config.additionalValue?.interpretation,
        };

        if (secDataPoint) {
          tooltipData.secondaryData = {
            label: secondaryDataArray.label,
            value: secDataPoint.value,
          };
        }
      }

      switch (config.breakdownMode) {
        case 'stacked':
        case 'fill-stacked': {
          const isFill = config.breakdownMode === 'fill-stacked';

          const columnTotal = tooltipData.breakdownData.reduce(
            (s, ele) => (isFill ? Math.max(0, ele.value) : ele.value) + s,
            0
          );

          if (isFill) {
            tooltipData.withShares = true;
            if (!secondaryDataArray) {
              tooltipData.headerData.sum = columnTotal;
            }
            yValue = 0.5;
          } else {
            yValue = columnTotal / 2;
          }

          break;
        }
        case 'parallel': {
          if (this.sortItems) {
            tooltipData.breakdownData.sort((a, b) => b.value - a.value);
          }

          if (config instanceof BarChartConfig) {
            const yMax = Math.max(...tooltipData.breakdownData.map(ele => ele.value));
            yValue = yMax / 2;
          } else {
            const ySum = tooltipData.breakdownData.map(ele => ele.value).reduce((a, b) => a + b, 0);
            yValue = ySum / tooltipData.breakdownData.length;
          }

          break;
        }
        default: // no breakdown configured
      }
    }

    const customAnchorValue = config.customTooltip?.getAnchorValue?.(tooltipData);
    const hasCustomAnchor = customAnchorValue !== undefined;

    if (customAnchorValue !== undefined) {
      yValue = customAnchorValue;
    }

    this.tooltipContent.setData(tooltipData);

    return { hasCustomAnchor, yValue: yValue! };
  }

  private buildSeriesTooltipData(
    index: number,
    config: LineChartConfig | BarChartConfig,
    limit: number | undefined,
    inverted: boolean | undefined
  ): ChartTooltipSeriesData[] {
    return (
      config.data?.map(dataArray => {
        const chartData = dataArray.getData(limit, inverted)[index];
        const valueConfig = dataArray.type === ChartDataType.secondary ? config.additionalValue : config.value;
        const unit = valueConfig?.unit;
        const unitInfo: MeasureUnitInfo | undefined =
          typeof unit === 'string' ? ({ code: unit as MeasureUnitCode } as MeasureUnitInfo) : unit;

        return {
          label: dataArray.label,
          dataLabel: chartData?.label,
          value: chartData?.value,
          seriesColor: dataArray.color ?? chartData?.color,
          includeInTooltip: dataArray.label.trim().length > 0,
          isComparison: dataArray.isComparison,
          isOther: dataArray.isOther,
          unit: unitInfo,
        };
      }) ?? []
    );
  }
}
